import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { Youtube, ArrowRight, Play } from "lucide-react";

export default function NoPressure() {
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/site/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/podcast/playlist")
      .then(({ data }) => {
        const list = data.items || [];
        setItems(list);
        if (list.length) setActiveId(list[0].video_id);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const channelUrl = settings?.youtube_channel_url || "https://www.youtube.com/@anartizen";
  const handle     = settings?.youtube_channel_handle || "@anartizen";
  const playlistId = settings?.youtube_playlist_id || "";
  const playlistUrl = playlistId ? `https://www.youtube.com/playlist?list=${playlistId}` : channelUrl;

  const active = items.find((i) => i.video_id === activeId) || items[0];
  const others = items.filter((i) => i.video_id !== activeId);

  return (
    <PageShell>
      {/* HERO */}
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="NO PRESSURE" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-16">
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-bold leading-[0.9] tracking-tighter">
            Loud, honest <br /> <span className="hl-coral">conversations.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-white/85 text-base md:text-lg leading-relaxed">
            No Pressure is where India&apos;s working creatives — musicians, filmmakers, designers, comedians — sit down and say the things they don&apos;t say on stage.
          </p>
          <a
            href={playlistUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="np-channel-cta"
            className="btn-pill btn-coral mt-8 inline-flex"
          >
            <Youtube size={14} /> Open the full playlist · {handle} <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* LATEST EPISODE */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
          <SectionLabel>Latest episode</SectionLabel>
          {loading ? (
            <p className="font-mono text-sm text-white/50">Loading episodes…</p>
          ) : active ? (
            <div className="border border-border bg-background" data-testid="np-latest">
              <iframe
                key={active.video_id}
                src={`https://www.youtube.com/embed/${active.video_id}?rel=0&modestbranding=1`}
                title={active.title}
                className="w-full aspect-video"
                allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
                data-testid="np-latest-iframe"
              />
              <div className="p-6 md:p-8 border-t border-border">
                <p className="meta-mono !text-secondary">
                  — {items.indexOf(active) === 0 ? "LATEST" : "NOW PLAYING"}
                  {active.published && ` · ${formatDate(active.published)}`}
                </p>
                <p className="font-display text-2xl md:text-4xl font-bold mt-3 leading-tight tracking-tight" data-testid="np-latest-title">
                  {active.title}
                </p>
                {active.description && (
                  <p className="mt-4 text-white/75 leading-relaxed max-w-3xl line-clamp-3">{active.description}</p>
                )}
                <a
                  href={active.watch_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-pill mt-6 inline-flex"
                  data-testid="np-latest-open"
                >
                  <Youtube size={14} /> Watch on YouTube <ArrowRight size={14} />
                </a>
              </div>
            </div>
          ) : (
            <a
              href={playlistUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="np-fallback"
              className="block border border-border bg-background p-10 group hover:border-primary transition"
            >
              <div className="aspect-[16/9] bg-[#0c0c0e] flex items-center justify-center border border-border mb-6 group-hover:border-primary/40 transition">
                <Youtube size={72} className="text-primary/70 group-hover:text-primary transition" />
              </div>
              <p className="font-display text-3xl md:text-4xl font-bold">Subscribe on YouTube.</p>
              <p className="meta-mono mt-4 !text-white/55">— NEW EPISODES DROP MONTHLY · {handle.toUpperCase()}</p>
            </a>
          )}
        </div>
      </section>

      {/* OTHER EPISODES (PLAYLIST) */}
      {others.length > 0 && (
        <section>
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <SectionLabel>The playlist · Other episodes</SectionLabel>
                <p className="font-display text-3xl md:text-4xl font-bold leading-tight tracking-tight">
                  Pick one. <span className="hl-cyan">Press play.</span>
                </p>
              </div>
              <a
                href={playlistUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-pill"
                data-testid="np-open-playlist"
              >
                Open playlist <ArrowRight size={13} />
              </a>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border" data-testid="np-playlist-grid">
              {others.map((e) => (
                <button
                  key={e.video_id}
                  onClick={() => {
                    setActiveId(e.video_id);
                    window.scrollTo({ top: 380, behavior: "smooth" });
                  }}
                  data-testid={`np-ep-${e.video_id}`}
                  className="bg-background group text-left"
                >
                  <div className="aspect-video overflow-hidden relative">
                    <img
                      src={e.thumbnail}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-300"
                    />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition">
                      <span className="w-14 h-14 rounded-full bg-primary flex items-center justify-center">
                        <Play size={22} className="ml-1 text-white" fill="white" />
                      </span>
                    </span>
                  </div>
                  <div className="p-5">
                    <p className="meta-mono !text-secondary">— {formatDate(e.published) || "EPISODE"}</p>
                    <p className="font-display text-lg md:text-xl font-bold mt-2 leading-tight tracking-tight line-clamp-2">
                      {e.title}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch (e) {
    return "";
  }
}
